import type { PrintSize } from "../nonogram/printSelection";

/**
 * What each printable page publishes.
 *
 * The ids are written down rather than drawn from a seed at render time, and that is
 * deliberate: these sheets are the ones a crawler indexes and a visitor prints, so
 * they have to be the same set on every build and on every machine. A seeded draw
 * would silently reprint a different sheet the next time the catalogue grew. Choosing
 * a new set is an edit here, visible in the diff.
 *
 * They were drawn with selectPrintable so the sets are not one person's favourites,
 * then pinned. The tests check the properties that matter — right size, printable
 * theme, no repeats, all present in the catalogue — rather than the exact draw, which
 * would only re-state this file.
 */
export interface PrintableSheet {
  size: PrintSize;
  /** Puzzle ids, in the order they are printed. */
  ids: string[];
}

/** The 5×5 printed on /printable-nonograms. A picture, not a glyph, so the answer
    sheet is a house rather than a letter. The id is the contract between the static
    HTML and the React page — both must load this grid, not a random one. */
export const PRINTABLE_PUZZLE_ID = "easy-5-02";

/** One sheet: four 5×5 grids two-up, the size that fits four to a page. */
export const PRINTABLE_SHEET_5: PrintableSheet = {
  size: 5,
  ids: ["easy-5-08", "easy-5-48", "easy-5-34", "easy-5-40"],
};

/** A 10×10 takes a page on its own, so this is two sheets plus the key. */
export const PRINTABLE_SHEET_10: PrintableSheet = {
  size: 10,
  ids: ["medium-10-65", "medium-10-166"],
};

export const PRINTABLE_SHEET_15: PrintableSheet = {
  size: 15,
  ids: ["hard-15-85", "hard-15-30"],
};

/** Eight 5×5 grids across two sheets — a set to hand out rather than one puzzle to
    solve, which is what someone printing for a class or a car journey is after. */
export const PRINTABLE_SHEET_KIDS: PrintableSheet = {
  size: 5,
  ids: ["easy-5-44", "easy-5-04", "easy-5-73", "easy-5-68", "easy-5-12", "easy-5-94", "easy-5-79", "easy-5-56"],
};
